import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common/States";
import { useSession } from "@/context/SessionContext";
import { errMsg, BRANCH_TYPES } from "@/lib/format";

const EMPTY = { name: "", code: "", type: "workshop", address: "", city: "", state: "", state_code: "", pincode: "", phone: "", gstin: "", active: true };
const TEXT = [["address", "Address"], ["city", "City"], ["state", "State"], ["state_code", "GST state code"], ["pincode", "PIN code"], ["phone", "Phone"], ["gstin", "Branch GSTIN"]];

export const BranchFormDialog = ({ open, onOpenChange, branch, onSaved }) => {
  const { api } = useSession();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(branch ? { ...EMPTY, ...Object.fromEntries(Object.entries(branch).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  }, [open, branch]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] === "" ? null : f[k]]));
    try {
      const { data } = branch ? await api.patch(`/branches/${branch.id}`, body, { noBranch: true }) : await api.post("/branches", body, { noBranch: true });
      toast.success(branch ? "Branch updated" : "Branch created");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="branch-form-dialog">
        <DialogHeader>
          <DialogTitle>{branch ? "Edit branch" : "Add branch"}</DialogTitle>
          <DialogDescription>Workshops, service centers, EV centers and warehouses.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <Field label="Branch name *"><Input required value={f.name} onChange={set("name")} data-testid="branch-name-input" /></Field>
          <Field label="Code *" hint="Unique within the company"><Input required value={f.code} onChange={set("code")} data-testid="branch-code-input" /></Field>
          <Field label="Type">
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="branch-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{BRANCH_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {TEXT.map(([k, label]) => <Field key={k} label={label}><Input value={f[k]} onChange={set(k)} data-testid={`branch-${k.replace("_", "-")}-input`} /></Field>)}
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={!!f.active} onCheckedChange={set("active")} data-testid="branch-active-switch" />
            <span className="text-sm">Active</span>
          </div>
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="branch-save-button">{saving ? "Saving…" : "Save branch"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
